<template>
  <InstanceNameEditModal
    :open="showNameEditModal"
    :close="handleCloseNameEditModal"
    :componentId="componentId"
    :instanceId="instanceId"
  />
  <ComponentPreviewModal
    :open="showComponentPreviewModal"
    :close="handleCloseComponentPreviewModal"
    :shortCode="`[${this.componentName
      .toLowerCase()
      .replaceAll(' ', '_')} name=${this.componentId}|${this.instanceName}]`"
  />
  <div
    class="rounded-t bg-white mb-0 px-6 py-6 flex justify-between uppercase font-bold items-center"
  >
    <button @click="handleGoBack" class="flex">
      <IconChevronLeft />{{ formattedTitle(title) }}
    </button>
    <transition
      enter-active-class="duration-300 ease-out"
      enter-from-class="transform opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="transform opacity-0"
      mode="out-in"
    >
      <div class="text-center flex justify-end items-center">
        <button
          :disabled="!hasInstance"
          @click="handleUpdate"
          class="inline-flex items-center bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
          :class="
            hasInstance
              ? 'bg-emerald-500 active:bg-emerald-600'
              : 'bg-gray-200 active:bg-gray-400'
          "
          type="button"
        >
          Save
          <IconSync class="ml-1 animate-spin" v-if="getSliderUpdateLoading" />
        </button>
        <button
          :disabled="!hasInstance || checkCopyExist()"
          @click="handleCopy"
          :class="
            hasInstance && !checkCopyExist()
              ? 'bg-gray-500 active:bg-gray-600'
              : 'bg-gray-200 active:bg-gray-400'
          "
          class="inline-flex items-center bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        >
          Duplicate
          <IconSync class="ml-1 animate-spin" v-if="getSliderCopyLoading" />
        </button>
        <button
          :disabled="!hasInstance"
          @click="handleEditName"
          :class="
            hasInstance
              ? 'bg-yellow-500 active:bg-yellow-600'
              : 'bg-gray-200 active:bg-gray-400'
          "
          type="button"
          class="flex items-center text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        >
          <IconPencilAlt class="w-4 h-4 m-0 mr-2" /> Edit name
        </button>
        <button
          :disabled="!hasInstance"
          :class="
            hasInstance
              ? 'bg-yellow-500 active:bg-yellow-600'
              : 'bg-gray-200 active:bg-gray-400'
          "
          @click="copyShortCode"
          type="button"
          class="w-[140px] flex items-center text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        >
          <IconCode class="w-4 h-4 m-0 mr-2" /> {{ shortCodeText }}
        </button>
        <button
          type="button"
          @click="showPreview"
          class="bg-sky-500 text-white active:bg-sky-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-4 ease-linear transition-all duration-150"
        >
          Live View
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import IconChevronLeft from "@/assets/icons/IconChevronLeft";
import IconSync from "@/assets/icons/IconSync";
import IconCode from "@/assets/icons/IconCode";
import IconPencilAlt from "@/assets/icons/IconPencilAlt";
import InstanceNameEditModal from "@/components/Shared/Modal/InstanceNameEditModal";
import ComponentPreviewModal from "@/components/Shared/Modal/ComponentPreviewModal";

export default {
  name: "ImageSliderUpdatePageHeader",
  components: {
    IconSync,
    IconCode,
    IconChevronLeft,
    IconPencilAlt,
    InstanceNameEditModal,
    ComponentPreviewModal
  },
  data() {
    return {
      showNameEditModal: false,
      showComponentPreviewModal: false,
      shortCodeText: "shortcode"
    };
  },
  props: {
    title: {
      type: String,
      required: true
    },
    componentName: {
      type: String,
      required: true
    },
    handleUpdate: {
      type: Function,
      required: true
    },
    handleCopy: {
      type: Function,
      required: true
    },
    componentId: {
      type: String,
      required: true
    },
    instanceId: {
      type: String,
      required: true
    }
  },
  computed: {
    instanceName() {
      if (
        this.getInstanceInfo &&
        this.getInstanceInfo.find((item) => item.id === this.instanceId)
      ) {
        return this.getInstanceInfo
          .find((item) => item.id === this.instanceId)
          .instance_name.split("|")[1];
      } else {
        return null;
      }
    },
    hasInstance() {
      if (this.getInstanceInfo) {
        return !!this.getInstanceInfo.length;
      } else {
        return false;
      }
    },
    instanceLocal: {
      get: function () {
        return this.instance;
      },
      set: function (val) {
        this.$emit("update:instance", val);
      }
    },
    ...mapGetters([
      "getAllAttributes",
      "getSliderUpdateLoading",
      "getSliderCopyLoading",
      "getInstanceInfo"
    ])
  },
  emits: ["update:instance"],
  methods: {
    formattedTitle(name) {
      if (name) {
        const splitName = name.split("|");
        splitName[1] = splitName[1].toLowerCase();
        if (splitName[1].includes("_") || splitName[1].includes("-")) {
          splitName[1] = splitName[1].replaceAll("_", " ");
          splitName[1] = splitName[1].replaceAll("-", " ");
        }
        return splitName[1];
      } else {
        return "Loading...";
      }
    },
    showPreview() {
      this.handleOpenComponentPreviewModal();
    },
    handleOpenNameEditModal() {
      this.showNameEditModal = true;
    },
    handleCloseNameEditModal() {
      this.showNameEditModal = false;
    },
    handleOpenComponentPreviewModal() {
      this.showComponentPreviewModal = true;
    },
    handleCloseComponentPreviewModal() {
      this.showComponentPreviewModal = false;
    },
    handleEditName() {
      this.handleOpenNameEditModal();
    },
    copyShortCode() {
      navigator.clipboard.writeText(
        `[${this.componentName.toLowerCase().replaceAll(" ", "_")} name=${
          this.componentId
        }|${this.instanceName}]`
      );
      this.shortCodeText = "copied";
      setTimeout(() => {
        this.shortCodeText = "shortcode";
      }, 3000);
    },
    handleGoBack() {
      this.$router.back();
    },
    checkCopyExist() {
      let copyExist = false;
      this.getInstanceInfo.forEach((item) => {
        if (item.instance_name === this.title + "_copy") {
          copyExist = true;
        }
      });
      return copyExist;
    }
  }
};
</script>
